import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'

// Types remain unchanged...
export type TaskType =
  | 'scan'
  | 'application'
  | 'doc_upload'
  | 'funding_need_doc_upload'

export type TaskStatus =
  | 'connecting'
  | 'starting'
  | 'running'
  | 'completed'
  | 'error'
  | 'cleaned_up'

export interface Task {
  id: string
  type: TaskType
  status: TaskStatus
  progress: number
  results_length: number
  error?: string
  message?: string
  startTime: string
  endTime?: string
  need_id?: string
  org_uuid?: string
  auth0_id?: string
  scan_id?: string
  queriesInvalidated?: boolean
}

// Context interface unchanged
interface TaskContextType {
  tasks: Record<string, Task>
  currentTasks: Record<TaskType, Task[]>
  startTask: (
    type: TaskType,
    taskId: string,
    needId?: string,
    orgUuid?: string,
    scanId?: string
  ) => void
  trackTaskProgress: (
    taskId: string,
    token: string
  ) => { unsubscribe: () => void }
  stopTrackingTask: (taskId: string) => void
  clearTask: (taskId: string) => void
  clearAllTasks: () => void
  getTasksByType: (type: TaskType) => Task[]
  findTaskByNeedId: (needId: string, type?: TaskType) => Task | null
  findActiveTaskByNeedId: (needId: string, type?: TaskType) => Task | null
  findTaskByScanId: (scanId: string, type?: TaskType) => Task | null
  findActiveTaskByScanId: (scanId: string, type?: TaskType) => Task | null
  isTaskActive: (task: Task | null) => boolean
  isTaskComplete: (task: Task | null) => boolean
  refreshTasksState: () => void
}

const TaskContext = createContext<TaskContextType | undefined>(undefined)

const serverURL = import.meta.env.VITE_API_URL || ''

// API ENDPOINTS remain unchanged
const API_ENDPOINTS = {
  scan: {
    progress: (id: string) => `${serverURL}/scans/progress/${id}`,
    results: (id: string) => `${serverURL}/scans/${id}/results`,
    // Lightweight status poll used to re-sync after an SSE drop (#1295).
    status: (id: string) => `${serverURL}/scans/${id}/status`,
  },
  application: {
    progress: (id: string) => `${serverURL}/applications/progress/${id}`,
    results: (id: string) => `${serverURL}/applications/${id}/results`,
    status: (id: string) => `${serverURL}/applications/status/${id}`,
  },
  doc_upload: {
    progress: (id: string) => `${serverURL}/orgs/uploads/progress/${id}`,
    results: (id: string) => `${serverURL}/orgs/uploads/${id}/status`,
    status: (id: string) => `${serverURL}/orgs/uploads/${id}/status`,
  },
  funding_need_doc_upload: {
    progress: (id: string) => `${serverURL}/funding-needs/uploads/progress/${id}`,
    results: (id: string) => `${serverURL}/funding-needs/uploads/${id}/status`,
    status: (id: string) => `${serverURL}/funding-needs/uploads/${id}/status`,
  },
}

// Key improvement 1: Track active connections and keep a registry of active subscribers
type ActiveConnection = {
  taskId: string
  eventSource: EventSource | null
  subscribers: Set<string> // Set of unique IDs for components subscribing to this task
  expiry?: number // Timestamp when this connection will auto-close if no subscribers
  generation: number
  dropEpoch: number
  terminal: boolean
  reconnectAttempts: number
  reconnectTimer: ReturnType<typeof setTimeout> | null
  connecting: boolean
  exhaustionChecking: boolean
  ensureConnected?: () => void
}

const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 30000] as const
const MAX_RECONNECT_ATTEMPTS = 12
const STATUS_FETCH_TIMEOUT_MS = 10000
export const RECONNECTING_MESSAGE =
  'Reconnecting… your scan is still running'

export const TaskProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient()
  const [tasks, setTasks] = useState<Record<string, Task>>({})
  const [currentTasks, setCurrentTasks] = useState<Record<TaskType, Task[]>>({
    scan: [],
    application: [],
    doc_upload: [],
    funding_need_doc_upload: [],
  })

  // Force refresh counter - increment to trigger state refresh
  const [refreshCounter, setRefreshCounter] = useState(0)

  // New: Use a ref to manage active connections to prevent stale closures
  const activeConnectionsRef = useRef<Record<string, ActiveConnection>>({})

  // New: Generate unique subscriber IDs
  const nextSubscriberIdRef = useRef(1)
  const getSubscriberId = () => `sub_${nextSubscriberIdRef.current++}`
  const nextConnectionGenerationRef = useRef(1)

  // checks if a task is active - unchanged
  const isTaskActive = useCallback((task: Task | null): boolean => {
    if (!task) return false
    return !['completed', 'error', 'cleaned_up'].includes(task.status)
  }, [])

  // checks if a task is complete - unchanged
  const isTaskComplete = useCallback((task: Task | null): boolean => {
    if (!task) return false
    return task.status === 'completed'
  }, [])

  // Function to manually refresh tasks state - unchanged
  const refreshTasksState = useCallback(() => {
    setRefreshCounter((prev) => prev + 1)
  }, [])

  // Load tasks from localStorage on mount and when refreshCounter changes
  // Logic remains similar with minor logging improvements
  useEffect(() => {
    try {
      const savedTasks = localStorage.getItem('tasks')
      if (savedTasks) {
        const parsedTasks = JSON.parse(savedTasks)

        // validate and fix any status inconsistencies
        const validatedTasks: Record<string, Task> = {}
        const now = new Date().getTime()
        Object.entries(parsedTasks).forEach(([id, taskData]) => {
          const task = taskData as Task

          // Skip cleaned_up tasks immediately - don't process them at all
          if (task.status === 'cleaned_up') {
            return
          }

          // check for stale tasks
          if (['connecting', 'starting', 'running'].includes(task.status)) {
            // check if the task is older than 20 minutes
            const startTime = new Date(task.startTime).getTime()
            const timeDiffMinutes = (now - startTime) / (1000 * 60)

            // if task has been in a non-terminal state for a while, mark it as error
            if (timeDiffMinutes > 20) {
              task.status = 'error'
              task.error = 'Task was interrupted'
              task.endTime = new Date().toISOString()
            }
          }

          // check for completed tasks that are older than 5 minutes
          if (task.status === 'completed') {
            const completedTime = task.endTime
              ? new Date(task.endTime).getTime()
              : 0
            const timeDiffMinutes = (now - completedTime) / (1000 * 60)

            // if completed more than 2 minutes ago, mark as cleaned_up
            if (completedTime > 0 && timeDiffMinutes > 2) {
              task.status = 'cleaned_up'
            }
          }

          // check for error tasks that are older than 1 minute
          if (task.status === 'error') {
            const errorTime = task.endTime
              ? new Date(task.endTime).getTime()
              : new Date(task.startTime).getTime()
            const timeDiffMinutes = (now - errorTime) / (1000 * 60)

            // if error occurred more than 1 minute ago, mark as cleaned_up
            if (timeDiffMinutes > 1) {
              task.status = 'cleaned_up'
            }
          }

          // check for invalid statuses
          if (
            ![
              'connecting',
              'starting',
              'running',
              'completed',
              'error',
              'cleaned_up',
            ].includes(task.status)
          ) {
            task.status = 'error' // default to error if invalid
          }

          // Add validated task to the collection
          validatedTasks[id] = task
        })

        setTasks(validatedTasks)

        // update currentTasks structure - only include active tasks
        const activeTasks: Record<TaskType, Task[]> = {
          scan: [],
          application: [],
          doc_upload: [],
          funding_need_doc_upload: [],
        }

        Object.values(validatedTasks).forEach((task: Task) => {
          // only add tasks that are not completed, error, or cleaned_up
          if (!['completed', 'error', 'cleaned_up'].includes(task.status)) {
            if (activeTasks[task.type]) {
              activeTasks[task.type].push(task)
            }
          }
        })

        setCurrentTasks(activeTasks)
      }
    } catch (error) {
      // if failed to load, start with empty state
      setTasks({})
      setCurrentTasks({
        scan: [],
        application: [],
        doc_upload: [],
        funding_need_doc_upload: [],
      })
    }
  }, [refreshCounter])

  // add periodic cleanup for completed tasks - unchanged
  useEffect(() => {
    const interval = setInterval(() => {
      setTasks((prevTasks) => {
        let hasUpdates = false
        const updatedTasks = { ...prevTasks }
        const now = new Date().getTime()

        // Check each task
        Object.entries(updatedTasks).forEach(([id, task]) => {
          // Check for completed tasks older than 5 minutes
          if (task.status === 'completed' && task.endTime) {
            const completedTime = new Date(task.endTime).getTime()
            const timeDiffMinutes = (now - completedTime) / (1000 * 60)

            // If completed more than 2 minutes ago, mark as cleaned_up
            if (timeDiffMinutes > 2) {
              updatedTasks[id] = {
                ...task,
                status: 'cleaned_up',
              }
              hasUpdates = true
            }
          }
        })

        // Only update state if changes were made
        return hasUpdates ? updatedTasks : prevTasks
      })
    }, 60000) // Check every minute

    return () => clearInterval(interval)
  }, [])

  // Task cleanup when connection is idle/inactive
  useEffect(() => {
    // Check for expired connections every 30 seconds
    const interval = setInterval(() => {
      const now = Date.now()
      const connections = activeConnectionsRef.current

      Object.keys(connections).forEach((taskId) => {
        const connection = connections[taskId]

        // If connection has expiry and no subscribers, close it
        if (
          connection.expiry &&
          now > connection.expiry &&
          connection.subscribers.size === 0
        ) {
          connection.terminal = true
          connection.generation += 1
          connection.dropEpoch += 1
          if (connection.reconnectTimer) {
            clearTimeout(connection.reconnectTimer)
          }
          connection.eventSource?.close()
          delete activeConnectionsRef.current[taskId]
        }
      })
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  // save tasks to localStorage when they change
  useEffect(() => {
    try {
      // limit localStorage to only recent tasks to prevent bloat
      const recentTasks: Record<string, Task> = {}
      const now = new Date()
      const taskEntries = Object.entries(tasks)

      // only keep last 50 tasks or tasks from last 24 hours
      const recentTaskEntries = taskEntries
        .sort(([, taskA], [, taskB]) => {
          return (
            new Date(taskB.startTime).getTime() -
            new Date(taskA.startTime).getTime()
          )
        })
        .filter(([, task], index) => {
          // Keep all active tasks
          if (isTaskActive(task)) return true

          // IMPORTANT CHANGE: Make sure we keep completed tasks to allow them to be checked for cleanup
          // on next load, even if they're more than an hour old
          if (task.status === 'completed') {
            // If it has an endTime, check if it's less than 3 minutes old
            // (slightly more than cleanup threshold to ensure it gets properly cleaned up)
            if (task.endTime) {
              const endTime = new Date(task.endTime).getTime()
              const minutesSinceEnd = (now.getTime() - endTime) / (1000 * 60)
              // Keep if less than 3 minutes since completion - giving time for cleanup cycle
              return minutesSinceEnd < 3
            }
            // If no endTime, keep it (something might be wrong)
            return true
          }

          // For cleaned_up tasks, don't keep them in localStorage at all
          if (task.status === 'cleaned_up') return false

          // For error tasks, keep only recent ones (last 5 minutes or last 50)
          const taskTime = new Date(task.startTime).getTime()
          const minutesDiff = (now.getTime() - taskTime) / (1000 * 60)
          return index < 50 || minutesDiff < 5
        })

      // convert back to object and save
      recentTaskEntries.forEach(([id, task]) => {
        recentTasks[id] = task
      })

      localStorage.setItem('tasks', JSON.stringify(recentTasks))
    } catch (error) {
      // TODO: figure out what type of error, full storage or private browser then handle with workarounds or  toaster notif
      console.error('Failed to save tasks to localStorage')
    }
  }, [tasks, isTaskActive])

  // start tracking a new task - unchanged
  const startTask = useCallback(
    (
      type: TaskType,
      taskId: string,
      needId?: string,
      orgUuid?: string,
      scanId?: string
    ) => {
      const newTask: Task = {
        id: taskId,
        type,
        status: 'starting',
        progress: 0,
        results_length: 0,
        startTime: new Date().toISOString(),
        need_id: needId,
        org_uuid: orgUuid,
        scan_id: scanId,
        auth0_id: undefined, // Will be populated from the server response
      }

      setTasks((prev) => ({
        ...prev,
        [taskId]: newTask,
      }))

      // add to current tasks of this type
      setCurrentTasks((prev) => {
        const updatedTasks = [...prev[type], newTask]
        return {
          ...prev,
          [type]: updatedTasks,
        }
      })
    },
    []
  )

  const trackTaskProgress = useCallback(
    (taskId: string, token: string) => {
      const subscriberId = getSubscriberId()
      const task = tasks[taskId]
      if (!task) return { unsubscribe: () => {} }

      const existingConnection = activeConnectionsRef.current[taskId]
      if (existingConnection) {
        existingConnection.subscribers.add(subscriberId)
        existingConnection.expiry = undefined
        existingConnection.ensureConnected?.()

        return {
          unsubscribe: () => {
            const connection = activeConnectionsRef.current[taskId]
            if (!connection) return
            connection.subscribers.delete(subscriberId)
            if (connection.subscribers.size === 0) {
              connection.expiry = Date.now() + 10000
            }
          },
        }
      }

      if (!['connecting', 'starting', 'running'].includes(task.status)) {
        return { unsubscribe: () => {} }
      }

      if (task.status !== 'connecting') {
        setTasks((prev) => ({
          ...prev,
          [taskId]: { ...prev[taskId], status: 'connecting' },
        }))
        setCurrentTasks((prev) => ({
          ...prev,
          [task.type]: prev[task.type].map((currentTask) =>
            currentTask.id === taskId
              ? { ...currentTask, status: 'connecting' }
              : currentTask
          ),
        }))
      }

      const activeConnection: ActiveConnection = {
        taskId,
        eventSource: null,
        subscribers: new Set([subscriberId]),
        generation: nextConnectionGenerationRef.current++,
        dropEpoch: 0,
        terminal: false,
        reconnectAttempts: 0,
        reconnectTimer: null,
        connecting: false,
        exhaustionChecking: false,
      }
      activeConnectionsRef.current[taskId] = activeConnection

      const endpoint = API_ENDPOINTS[task.type].progress(taskId)
      const statusEndpoint = API_ENDPOINTS[task.type].status(taskId)

      const isCurrent = (generation: number, dropEpoch?: number) => {
        const current = activeConnectionsRef.current[taskId]
        return (
          current === activeConnection &&
          current.generation === generation &&
          (dropEpoch === undefined || current.dropEpoch === dropEpoch) &&
          !current.terminal
        )
      }

      const clearReconnectTimer = () => {
        if (activeConnection.reconnectTimer) {
          clearTimeout(activeConnection.reconnectTimer)
          activeConnection.reconnectTimer = null
        }
      }

      const updateReconnectingState = () => {
        setTasks((prev) => {
          const currentTask = prev[taskId]
          if (
            !currentTask ||
            ['completed', 'error', 'cleaned_up'].includes(currentTask.status)
          ) {
            return prev
          }
          return {
            ...prev,
            [taskId]: {
              ...currentTask,
              status: 'running',
              message: RECONNECTING_MESSAGE,
            },
          }
        })
        setCurrentTasks((prev) => ({
          ...prev,
          [task.type]: prev[task.type].map((currentTask) =>
            currentTask.id === taskId
              ? {
                  ...currentTask,
                  status: 'running',
                  message: RECONNECTING_MESSAGE,
                }
              : currentTask
          ),
        }))
      }

      const applyMessage = (
        eventSource: EventSource | null,
        event: MessageEvent
      ) => {
        if (!isCurrent(activeConnection.generation)) return

        try {
          const data = JSON.parse(event.data)
          const isTerminal = ['completed', 'error', 'cleaned_up'].includes(
            data.status
          )

          if (isTerminal) {
            activeConnection.terminal = true
          } else {
            activeConnection.reconnectAttempts = 0
          }
          clearReconnectTimer()

          setTasks((prev) => {
            if (!prev[taskId]) return prev

            const updatedTask: Task = {
              ...prev[taskId],
              status: data.status || prev[taskId].status,
              progress:
                data.progress !== undefined
                  ? data.progress
                  : prev[taskId].progress,
              message:
                data.message ??
                (prev[taskId].message === RECONNECTING_MESSAGE
                  ? undefined
                  : prev[taskId].message),
            }

            if (data.results_length !== undefined) {
              updatedTask.results_length = data.results_length
            }
            if (data.error) updatedTask.error = data.error

            if (isTerminal) {
              updatedTask.endTime = new Date().toISOString()
              if (
                data.status === 'completed' &&
                prev[taskId].status !== 'completed'
              ) {
                updatedTask.queriesInvalidated = false
                if (
                  updatedTask.type === 'application' &&
                  updatedTask.need_id &&
                  updatedTask.org_uuid
                ) {
                  queryClient.invalidateQueries({
                    queryKey: [
                      'applications',
                      'need',
                      parseInt(updatedTask.need_id),
                      updatedTask.org_uuid,
                    ],
                  })
                  queryClient.invalidateQueries({
                    queryKey: [
                      'applications',
                      'summary',
                      updatedTask.org_uuid,
                    ],
                  })
                } else if (
                  updatedTask.type === 'scan' &&
                  updatedTask.need_id
                ) {
                  queryClient.invalidateQueries({
                    queryKey: [
                      'scanResultsByNeedId',
                      parseInt(updatedTask.need_id),
                    ],
                  })
                  queryClient.invalidateQueries({
                    queryKey: ['scanIds', parseInt(updatedTask.need_id)],
                  })
                  // A finished scan spends the org's allowance, so the cached
                  // count is now wrong — leaving it stale keeps re-scan controls
                  // enabled past the cap until a refetch or remount. Prefix
                  // match: scan tasks don't always carry org_uuid.
                  queryClient.invalidateQueries({
                    queryKey: ['orgScanUsage'],
                  })
                  // A zero-candidate scan writes last_scanned_at but no
                  // scan_results row, so it's only visible through the
                  // funding-need object itself -- without this, a mounted
                  // staleness check keeps reading the pre-scan value until an
                  // unrelated refetch happens. Prefix match, same reason as
                  // orgScanUsage above.
                  queryClient.invalidateQueries({
                    queryKey: ['fundingNeeds'],
                  })
                }
                updatedTask.queriesInvalidated = true
              }
            }

            return { ...prev, [taskId]: updatedTask }
          })

          setCurrentTasks((prev) => ({
            ...prev,
            [task.type]: isTerminal
              ? prev[task.type].filter(
                  (currentTask) => currentTask.id !== taskId
                )
              : prev[task.type].map((currentTask) =>
                  currentTask.id === taskId
                    ? {
                        ...currentTask,
                        status: data.status || currentTask.status,
                        progress:
                          data.progress !== undefined
                            ? data.progress
                            : currentTask.progress,
                        results_length:
                          data.results_length !== undefined
                            ? data.results_length
                            : currentTask.results_length,
                        message:
                          data.message ??
                          (currentTask.message === RECONNECTING_MESSAGE
                            ? undefined
                            : currentTask.message),
                        error: data.error || currentTask.error,
                      }
                    : currentTask
                ),
          }))

          if (isTerminal) {
            activeConnection.generation += 1
            activeConnection.dropEpoch += 1
            eventSource?.close()
            activeConnection.eventSource = null
            if (activeConnectionsRef.current[taskId] === activeConnection) {
              delete activeConnectionsRef.current[taskId]
            }
          }
        } catch {
          console.error('Error parsing SSE event')
        }
      }

      type StatusResult =
        | { kind: 'terminal'; data: Record<string, unknown> }
        | { kind: 'running'; data: Record<string, unknown> }
        | { kind: 'unreachable' }
        | { kind: 'stale' }

      const fetchTaskStatus = async (
        generation: number,
        dropEpoch: number
      ): Promise<StatusResult> => {
        const controller = new AbortController()
        const timeout = setTimeout(
          () => controller.abort(),
          STATUS_FETCH_TIMEOUT_MS
        )
        try {
          const response = await fetch(statusEndpoint, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          })
          if (!isCurrent(generation, dropEpoch)) return { kind: 'stale' }
          if (!response.ok) return { kind: 'unreachable' }

          const data = await response.json()
          if (!isCurrent(generation, dropEpoch)) return { kind: 'stale' }
          if (!data || typeof data !== 'object') {
            return { kind: 'unreachable' }
          }
          const status = (data as { status?: unknown }).status
          if (
            typeof status === 'string' &&
            ['completed', 'error', 'cleaned_up'].includes(status)
          ) {
            return {
              kind: 'terminal',
              data: data as Record<string, unknown>,
            }
          }
          if (
            typeof status !== 'string' ||
            !['connecting', 'starting', 'queued', 'running'].includes(status)
          ) {
            return { kind: 'unreachable' }
          }
          if (status === 'running') {
            activeConnection.reconnectAttempts = 0
          }
          return { kind: 'running', data: data as Record<string, unknown> }
        } catch {
          return isCurrent(generation, dropEpoch)
            ? { kind: 'unreachable' }
            : { kind: 'stale' }
        } finally {
          clearTimeout(timeout)
        }
      }

      const finishConnectionError = async (
        generation: number,
        dropEpoch: number
      ) => {
        if (
          !isCurrent(generation, dropEpoch) ||
          activeConnection.exhaustionChecking
        ) {
          return
        }
        activeConnection.exhaustionChecking = true
        try {
          // Retry exhaustion is still a transport conclusion, not proof that the
          // job failed. Re-sync one final time in case it completed during the
          // reconnect window before surfacing a connection error.
          const finalStatus = await fetchTaskStatus(generation, dropEpoch)
          if (!isCurrent(generation, dropEpoch)) return
          if (finalStatus.kind === 'terminal') {
            applyMessage(null, {
              data: JSON.stringify(finalStatus.data),
            } as MessageEvent)
            return
          }
          if (finalStatus.kind === 'running') {
            // Only an explicit `running` status resets the retry counter in
            // fetchTaskStatus. Other active states are valid job data, but they
            // are not proof that stream tracking recovered; after the bounded
            // window they must not recurse back into an already-exhausted
            // scheduler and leave the task stuck reconnecting forever.
            if (activeConnection.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
              scheduleReconnect(generation, dropEpoch)
              return
            }
          }
          if (finalStatus.kind === 'stale') return
          applyMessage(null, {
            data: JSON.stringify({
              status: 'error',
              error: 'Connection lost. Please try again.',
            }),
          } as MessageEvent)
        } finally {
          activeConnection.exhaustionChecking = false
        }
      }

      const scheduleReconnect = (generation: number, dropEpoch: number) => {
        if (!isCurrent(generation, dropEpoch)) return
        clearReconnectTimer()
        updateReconnectingState()

        if (activeConnection.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          void finishConnectionError(generation, dropEpoch)
          return
        }

        const attempt = activeConnection.reconnectAttempts
        const delay =
          RECONNECT_DELAYS_MS[
            Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
          ]
        activeConnection.reconnectAttempts += 1
        activeConnection.reconnectTimer = setTimeout(() => {
          activeConnection.reconnectTimer = null
          if (
            !isCurrent(generation, dropEpoch) ||
            activeConnection.subscribers.size === 0
          ) {
            return
          }
          void connect(generation, dropEpoch)
        }, delay)
      }

      async function connect(generation: number, dropEpoch: number) {
        if (!isCurrent(generation, dropEpoch) || activeConnection.connecting) {
          return
        }
        activeConnection.connecting = true

        const controller = new AbortController()
        const timeout = setTimeout(
          () => controller.abort(),
          STATUS_FETCH_TIMEOUT_MS
        )
        try {
          const response = await fetch(endpoint, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          })
          if (
            !isCurrent(generation, dropEpoch) ||
            activeConnection.subscribers.size === 0
          ) {
            return
          }
          if (!response.ok) {
            scheduleReconnect(generation, dropEpoch)
            return
          }

          const sseUrl = new URL(response.url)
          sseUrl.searchParams.set('token', token)
          const eventSource = new EventSource(sseUrl.toString(), {
            withCredentials: true,
          })
          if (!isCurrent(generation, dropEpoch)) {
            eventSource.close()
            return
          }

          activeConnection.eventSource?.close()
          activeConnection.eventSource = eventSource
          eventSource.onmessage = (event) => {
            if (activeConnection.eventSource !== eventSource) return
            applyMessage(eventSource, event)
          }
          eventSource.onerror = async () => {
            if (
              activeConnection.eventSource !== eventSource ||
              !isCurrent(activeConnection.generation)
            ) {
              return
            }

            activeConnection.dropEpoch += 1
            const errorGeneration = activeConnection.generation
            const errorEpoch = activeConnection.dropEpoch
            // Deliberately take over from EventSource's native retry for every
            // transport error. One explicit scheduler gives us bounded backoff,
            // a retry ceiling, and generation/epoch cancellation in one place.
            eventSource.close()
            if (activeConnection.eventSource === eventSource) {
              activeConnection.eventSource = null
            }
            updateReconnectingState()

            const statusResult = await fetchTaskStatus(
              errorGeneration,
              errorEpoch
            )
            if (!isCurrent(errorGeneration, errorEpoch)) return
            if (statusResult.kind === 'terminal') {
              applyMessage(eventSource, {
                data: JSON.stringify(statusResult.data),
              } as MessageEvent)
              return
            }
            if (statusResult.kind === 'stale') return
            scheduleReconnect(errorGeneration, errorEpoch)
          }
        } catch {
          if (isCurrent(generation, dropEpoch)) {
            scheduleReconnect(generation, dropEpoch)
          }
        } finally {
          activeConnection.connecting = false
          clearTimeout(timeout)
        }
      }

      activeConnection.ensureConnected = () => {
        if (
          !isCurrent(activeConnection.generation, activeConnection.dropEpoch) ||
          activeConnection.connecting ||
          activeConnection.eventSource ||
          activeConnection.reconnectTimer
        ) {
          return
        }
        scheduleReconnect(
          activeConnection.generation,
          activeConnection.dropEpoch
        )
      }

      void connect(activeConnection.generation, activeConnection.dropEpoch)

      return {
        unsubscribe: () => {
          const connection = activeConnectionsRef.current[taskId]
          if (!connection) return
          connection.subscribers.delete(subscriberId)
          if (connection.subscribers.size === 0) {
            connection.expiry = Date.now() + 10000
          }
        },
      }
    },
    [queryClient, tasks]
  )

  // stop tracking task
  const stopTrackingTask = useCallback((taskId: string) => {
    const connection = activeConnectionsRef.current[taskId]
    if (connection) {
      // close the EventSource
      connection.terminal = true
      connection.generation += 1
      connection.dropEpoch += 1
      if (connection.reconnectTimer) clearTimeout(connection.reconnectTimer)
      connection.eventSource?.close()
      delete activeConnectionsRef.current[taskId]

      // Update task status to indicate tracking has stopped
      setTasks((prev) => {
        if (!prev[taskId]) return prev

        // Only update if task is in a non-terminal state
        if (
          ['connecting', 'starting', 'running'].includes(prev[taskId].status)
        ) {
          return {
            ...prev,
            [taskId]: {
              ...prev[taskId],
              message: 'Tracking stopped due to explicit request',
              // Keep the current status so we can resume later
            },
          }
        }
        return prev
      })
    }
  }, [])

  // UPDATED: clearTask now properly cleans up connections
  const clearTask = useCallback(
    (taskId: string) => {
      // Close any active connection
      const connection = activeConnectionsRef.current[taskId]
      if (connection) {
        connection.terminal = true
        connection.generation += 1
        connection.dropEpoch += 1
        if (connection.reconnectTimer) clearTimeout(connection.reconnectTimer)
        connection.eventSource?.close()
        delete activeConnectionsRef.current[taskId]
      }

      // get the task to determine its type
      const task = tasks[taskId]
      if (!task) return

      setTasks((prev) => {
        const newTasks = { ...prev }
        delete newTasks[taskId]
        return newTasks
      })

      // also remove from current tasks
      setCurrentTasks((prev) => {
        const filteredArray = prev[task.type].filter((t) => t.id !== taskId)
        return {
          ...prev,
          [task.type]: filteredArray,
        }
      })
    },
    [tasks]
  )

  // UPDATED: clearAllTasks now properly cleans up connections
  const clearAllTasks = useCallback(() => {
    // close all event sources
    Object.keys(activeConnectionsRef.current).forEach((taskId) => {
      const connection = activeConnectionsRef.current[taskId]
      connection.terminal = true
      connection.generation += 1
      connection.dropEpoch += 1
      if (connection.reconnectTimer) clearTimeout(connection.reconnectTimer)
      connection.eventSource?.close()
    })
    // Reset active connections
    activeConnectionsRef.current = {}

    setTasks({})
    setCurrentTasks({
      scan: [],
      application: [],
      doc_upload: [],
      funding_need_doc_upload: [],
    })
  }, [])

  // Remaining methods are unchanged
  const getTasksByType = useCallback(
    (type: TaskType) => {
      return currentTasks[type] || []
    },
    [currentTasks]
  )

  const findTaskByNeedId = useCallback(
    (needId: string, type?: TaskType) => {
      if (!needId) return null

      // convert tasks object to array
      const tasksArray = Object.values(tasks)

      // filter by need_id and optionally by type
      const filteredTasks = tasksArray.filter(
        (task) => task.need_id === needId && (type ? task.type === type : true)
      )

      // sort by start time (newest first)
      filteredTasks.sort(
        (a, b) =>
          new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
      )

      // return the most recent matching task or null
      return filteredTasks.length > 0 ? filteredTasks[0] : null
    },
    [tasks]
  )

  const findActiveTaskByNeedId = useCallback(
    (needId: string, type?: TaskType) => {
      if (!needId) return null

      // convert tasks object to array
      const tasksArray = Object.values(tasks)

      // filter by need_id, type, and active status
      const filteredTasks = tasksArray.filter(
        (task) =>
          task.need_id === needId &&
          (type ? task.type === type : true) &&
          !['completed', 'error', 'cleaned_up'].includes(task.status)
      )

      // sort by start time (newest first)
      filteredTasks.sort(
        (a, b) =>
          new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
      )

      // return the most recent matching active task or null
      return filteredTasks.length > 0 ? filteredTasks[0] : null
    },
    [tasks]
  )

  const findTaskByScanId = useCallback(
    (scanId: string, type?: TaskType) => {
      if (!scanId) return null

      // convert tasks object to array
      const tasksArray = Object.values(tasks)

      // filter by scan_id and optionally by type
      const filteredTasks = tasksArray.filter(
        (task) => task.scan_id === scanId && (type ? task.type === type : true)
      )

      // sort by start time (newest first)
      filteredTasks.sort(
        (a, b) =>
          new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
      )

      // return the most recent matching task or null
      return filteredTasks.length > 0 ? filteredTasks[0] : null
    },
    [tasks]
  )

  const findActiveTaskByScanId = useCallback(
    (scanId: string, type?: TaskType) => {
      if (!scanId) return null

      // convert tasks object to array
      const tasksArray = Object.values(tasks)

      // filter by need_id, type, and active status
      const filteredTasks = tasksArray.filter(
        (task) =>
          task.scan_id === scanId &&
          (type ? task.type === type : true) &&
          !['completed', 'error', 'cleaned_up'].includes(task.status)
      )

      // sort by start time (newest first)
      filteredTasks.sort(
        (a, b) =>
          new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
      )

      // return the most recent matching active task or null
      return filteredTasks.length > 0 ? filteredTasks[0] : null
    },
    [tasks]
  )

  // Clean up all connections when provider unmounts
  useEffect(() => {
    return () => {
      // Close all remaining connections
      Object.values(activeConnectionsRef.current).forEach((connection) => {
        connection.terminal = true
        connection.generation += 1
        connection.dropEpoch += 1
        if (connection.reconnectTimer) clearTimeout(connection.reconnectTimer)
        connection.eventSource?.close()
      })

      // Reset the ref
      activeConnectionsRef.current = {}
    }
  }, [])

  return (
    <TaskContext.Provider
      value={{
        tasks,
        currentTasks,
        startTask,
        trackTaskProgress,
        stopTrackingTask,
        clearTask,
        clearAllTasks,
        getTasksByType,
        findTaskByNeedId,
        findActiveTaskByNeedId,
        findTaskByScanId,
        findActiveTaskByScanId,
        isTaskActive,
        isTaskComplete,
        refreshTasksState,
      }}
    >
      {children}
    </TaskContext.Provider>
  )
}

// TASK CONTEXT HOOK - unchanged
export const useTaskContext = () => {
  const context = useContext(TaskContext)
  if (context === undefined) {
    throw new Error('useTaskContext must be used within a TaskProvider')
  }
  return context
}
